import type { ModeDefinition, Param, RenderSettings } from './types';
import { nightSkyMode } from './night-sky';
import { daySkyMode } from './day-sky';

export * from './types';

// All registered modes
export const modes: ModeDefinition[] = [
  nightSkyMode,
  daySkyMode,
];

export function getModeById(id: string): ModeDefinition | undefined {
  return modes.find(m => m.id === id);
}

export function getModeByIndex(index: number): ModeDefinition | undefined {
  return modes.find(m => m.modeIndex === index);
}

// Get default values for a mode
export function getDefaultValues(mode: ModeDefinition): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const param of mode.params) {
    values[param.id] = param.default;
  }
  return values;
}

// Get default lock states for a mode
export function getDefaultLocks(mode: ModeDefinition): Set<string> {
  const locked = new Set<string>();
  for (const param of mode.params) {
    if (param.lockedByDefault) {
      locked.add(param.id);
    }
  }
  return locked;
}

// Convert mode values to render settings format expected by worker
export function toRenderSettings(
  mode: ModeDefinition,
  values: Record<string, unknown>,
  seed: string,
  hdrMode: boolean = false,
  sphericalMode: boolean = false,
): RenderSettings {
  const settings: RenderSettings = {
    mode: mode.modeIndex,
    seed: seed || 'default',
    hdrMode,
    sphericalMode,
    // Default empty ramps (will be overridden if present)
    ramps: {
      primary: ['#000000', '#000000', '#000000'] as [string, string, string],
      secondary: ['#000000', '#000000', '#000000'] as [string, string, string],
      accent: ['#000000', '#000000', '#000000'] as [string, string, string],
    },
    palette: ['#000000', '#000000', '#000000'] as [string, string, string],
    multiHue: false,
    variation: 0,
    nebula: 0,
    coverage: 0,
    detail: 1,
    starDensity: 0,
    starGlow: 0,
    heroCount: 50,
    heroBrightness: 0.5,
    heroSize: 0.5,
    sunEnabled: true,
    sunCount: 1,
    sunSizeMin: 0.4,
    sunSizeMax: 0.6,
    sunIntensity: 0.7,
    contrast: 1,
    gamma: 1,
    haze: 0,
  };

  // Map mode-specific params to render settings
  for (const param of mode.params) {
    const value = values[param.id];

    switch (param.id) {
      case 'primaryRamp':
        if (Array.isArray(value) && value.length === 3) {
          settings.ramps.primary = value as [string, string, string];
          settings.palette = value as [string, string, string];
          settings.multiHue = true;
        }
        break;
      case 'secondaryRamp':
        if (Array.isArray(value) && value.length === 3) {
          settings.ramps.secondary = value as [string, string, string];
        }
        break;
      case 'accentRamp':
        if (Array.isArray(value) && value.length === 3) {
          settings.ramps.accent = value as [string, string, string];
        }
        break;
      case 'variation':
        settings.variation = value as number;
        break;
      case 'nebula':
        settings.nebula = value as number;
        break;
      case 'coverage':
        settings.coverage = value as number;
        break;
      case 'detail':
        settings.detail = value as number;
        break;
      case 'starDensity':
        settings.starDensity = value as number;
        break;
      case 'starGlow':
        settings.starGlow = value as number;
        break;
      case 'heroCount':
        settings.heroCount = value as number;
        break;
      case 'heroBrightness':
        settings.heroBrightness = value as number;
        break;
      case 'heroSize':
        settings.heroSize = value as number;
        break;
      case 'sunEnabled':
        settings.sunEnabled = value as boolean;
        break;
      case 'sunCount':
        settings.sunCount = value as number;
        break;
      case 'sunSizeMin':
        settings.sunSizeMin = value as number;
        break;
      case 'sunSizeMax':
        settings.sunSizeMax = value as number;
        break;
      case 'sunIntensity':
        settings.sunIntensity = value as number;
        break;
      case 'contrast':
        settings.contrast = value as number;
        break;
      case 'gamma':
        settings.gamma = value as number;
        break;
      case 'haze':
        settings.haze = value as number;
        break;
    }
  }

  return settings;
}
